import { action, makeObservable, observable, reaction } from 'mobx';
import { ShopStore } from 'stores';

/**
 * Feature Flag Store for managing feature toggles
 * Features can be controlled globally or per-shop
 * If a flag is not set, it defaults to false
 */
class FeatureFlagStoreC {
  shopFlags = {};

  globalFlags = {};

  constructor() {
    makeObservable(this, {
      shopFlags: observable,
      globalFlags: observable,
      setShopFlag: action,
      setGlobalFlag: action,
      loadShopFlags: action,
    });

    this.loadGlobalFlags();

    // Defer shop change reaction setup to avoid circular dependency
    // Use setTimeout to ensure all stores are initialized first
    setTimeout(() => {
      this.setupShopChangeReaction();
    }, 0);
  }

  /**
   * Load global feature flags from environment variables
   */
  loadGlobalFlags = () => {
    this.globalFlags = {
      apiCaching: import.meta.env.VITE_FEATURE_API_CACHING === 'true',
      debugMode:
        import.meta.env.VITE_FEATURE_DEBUG_MODE === 'true' ||
        import.meta.env.MODE === 'development',
    };
  };

  /**
   * Set up MobX reaction to watch for shop changes
   */
  setupShopChangeReaction = () => {
    reaction(
      () => ShopStore.currentShop?.id,
      (newShopId, oldShopId) => {
        if (newShopId !== oldShopId && newShopId && newShopId !== -1) {
          console.log(
            `🔄 Shop changed from ${oldShopId} to ${newShopId}, reloading feature flags...`
          );
          this.loadShopFlags(newShopId);
        }
      },
      {
        fireImmediately: true,
        name: 'FeatureFlagStore.shopChangeReaction',
      }
    );
  };

  /**
   * Load feature flags for a specific shop
   * @param {number} shopId
   */
  loadShopFlags = async (shopId = ShopStore.currentShop?.id) => {
    if (!shopId) return;

    try {
      const shopSettings = await ShopStore.getShopSettings(shopId);
      const featureFlags =
        shopSettings?.result?.[0]?.settings?.featureFlags || {};

      this.shopFlags = typeof featureFlags === 'object' ? featureFlags : {};

      console.log(
        `🏪 Loaded feature flags for shop ${shopId}:`,
        this.shopFlags
      );
    } catch (error) {
      console.warn('Failed to load shop feature flags, clearing flags:', error);
      this.shopFlags = {};
    }
  };

  /**
   * Save feature flags for the current shop
   * @param {object} flags
   * @param {number} shopId
   */
  saveShopFlags = async (flags, shopId = ShopStore.currentShop?.id) => {
    if (!shopId) {
      throw new Error('No shop ID provided');
    }

    try {
      const currentSettings = await ShopStore.getShopSettings(shopId);
      const existingSettings = currentSettings?.result?.[0]?.settings || {};
      const existingFeatureFlags = existingSettings.featureFlags || {};

      // Merge new flags with existing flags (don't override entire settings)
      const updatedFeatureFlags = {
        ...existingFeatureFlags,
        ...flags,
      };

      // Create updated settings object, preserving all existing settings
      const updatedSettings = {
        ...existingSettings,
        featureFlags: updatedFeatureFlags,
      };

      await ShopStore.upsertShopSettings(shopId, updatedSettings);

      this.shopFlags = {
        ...this.shopFlags,
        ...flags,
      };

      console.log(`💾 Saved feature flags for shop ${shopId}:`, {
        updated: flags,
        allFlags: this.shopFlags,
        preservedSettings: Object.keys(existingSettings).filter(
          (key) => key !== 'featureFlags'
        ),
      });
    } catch (error) {
      console.error('Failed to save shop feature flags:', error);
      throw error;
    }
  };

  /**
   * Set a single shop flag
   * @param {string} flagName
   * @param {boolean} value
   * @param {number} shopId
   */
  setShopFlag = async (flagName, value, shopId = ShopStore.currentShop?.id) => {
    await this.saveShopFlags({ [flagName]: value }, shopId);
  };

  /**
   * Set a global flag (for development/testing)
   * @param {string} flagName
   * @param {boolean} value
   */
  setGlobalFlag = (flagName, value) => {
    this.globalFlags[flagName] = value;
    console.log(`🌍 Set global flag ${flagName}:`, value);
  };

  /**
   * Check if a feature is enabled (shop flags override global flags)
   * If flag is not set anywhere, defaults to false
   * @param {string} flagName
   * @returns {boolean}
   */
  isEnabled = (flagName) => {
    // Shop-specific flag takes precedence
    if (Object.hasOwn(this.shopFlags, flagName)) {
      return this.shopFlags[flagName];
    }

    // Fall back to global flag
    if (Object.hasOwn(this.globalFlags, flagName)) {
      return this.globalFlags[flagName];
    }

    // If not set anywhere, default to false
    return false;
  };

  /**
   * Convenience methods for specific features
   * @returns {boolean}
   */
  get isApiCachingEnabled() {
    return this.isEnabled('apiCaching');
  }

  get isDebugModeEnabled() {
    return this.isEnabled('debugMode');
  }

  get isShowRawDataMenuEnabled() {
    return this.isEnabled('showRawDataMenu');
  }

  /**
   * Get all current flags (for debugging/admin UI)
   * @returns {object}
   */
  getAllFlags = () => {
    return {
      global: this.globalFlags,
      shop: this.shopFlags,
    };
  };

  /**
   * Reset shop flags to defaults
   * @param {number} shopId
   */
  resetShopFlags = async (shopId = ShopStore.currentShop?.id) => {
    const defaultFlags = {
      apiCaching: false,
      debugMode: false,
      showRawDataMenu: false,
    };

    await this.saveShopFlags(defaultFlags, shopId);
  };
}

export const FeatureFlagStore = new FeatureFlagStoreC();
export default FeatureFlagStore;
